import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "../../api";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from "../ui/sonner";
import { INTEGRATIONS } from "../../constants/testIds";
import { SubmitButton } from "../common/SubmitButton";
import { useSuccessPulse } from "../../hooks/useSuccessPulse";

// UX-CP-02 §2 SCR-P08-20 "rotate credentials" — never accepts the OLD value back, only a new
// one; the server never returns plaintext either way (secrets_service.mask_secret). WhatsApp
// (Prompt 13) needs 2 fields (`access_token`+`app_secret`, both required by every real Graph API
// call) — every other channel still just takes the one generic `credential` value.
export function RotateSecretDialog({ orgId, config, open, onOpenChange }) {
  const [value, setValue] = useState("");
  const [accessToken, setAccessToken] = useState("");
  const [appSecret, setAppSecret] = useState("");
  const queryClient = useQueryClient();
  const [success, pulse] = useSuccessPulse();
  const isWhatsapp = config?.channel === "whatsapp";

  const rotateMutation = useMutation({
    mutationFn: () => api.post(`/control/gyms/${orgId}/integrations/${config.id}/rotate`, {
      secret: isWhatsapp ? { access_token: accessToken, app_secret: appSecret } : { credential: value },
    }),
    onSuccess: () => pulse(() => {
      toast.success(`${config.provider} credential rotated`);
      queryClient.invalidateQueries({ queryKey: ["gym-integrations", orgId] });
      setValue(""); setAccessToken(""); setAppSecret("");
      onOpenChange(false);
    }),
    onError: (e) => toast.error(formatApiError(e).message),
  });

  const canSubmit = isWhatsapp ? accessToken.trim() && appSecret.trim() : !!value;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-modal" data-testid={INTEGRATIONS.rotateDialog}>
        <DialogHeader>
          <DialogTitle>Rotate {config?.provider} credential</DialogTitle>
          <DialogDescription>The old credential is discarded immediately — this connector will use the new value from now on, and will need to be tested again before it's usable.</DialogDescription>
        </DialogHeader>
        {isWhatsapp ? (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="rotate-access-token-input">New access token <span style={{ color: "var(--scarlet-neg)" }}>*</span></Label>
              <Input id="rotate-access-token-input" type="password" data-testid={INTEGRATIONS.rotateAccessTokenInput} value={accessToken} onChange={(e) => setAccessToken(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rotate-app-secret-input">New app secret <span style={{ color: "var(--scarlet-neg)" }}>*</span></Label>
              <Input id="rotate-app-secret-input" type="password" data-testid={INTEGRATIONS.rotateAppSecretInput} value={appSecret} onChange={(e) => setAppSecret(e.target.value)} />
            </div>
          </div>
        ) : (
          <div className="space-y-1.5">
            <Label htmlFor="rotate-secret-input">New credential value <span style={{ color: "var(--scarlet-neg)" }}>*</span></Label>
            <Input id="rotate-secret-input" type="password" data-testid={INTEGRATIONS.rotateInput} value={value} onChange={(e) => setValue(e.target.value)} />
          </div>
        )}
        <DialogFooter>
          <SubmitButton testId={INTEGRATIONS.rotateSubmit} disabled={!canSubmit} pending={rotateMutation.isPending} success={success} onClick={() => rotateMutation.mutate()} label="Rotate" successLabel="Rotated" />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
